import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingIcon,
  Place as LocationIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Error as RejectedIcon,
  Speed as ProgressIcon,
  DoneAll as CompletedIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const StatisticsPanel = ({ statistics, geoData, loading }) => {
  
  // Color mapping
  const statusColors = {
    'pending': '#FFA500',
    'in_progress': '#2196F3',
    'approved': '#4CAF50',
    'completed': '#8BC34A',
    'rejected': '#F44336'
  };
  
  // Prepare chart data for status breakdown
  const prepareStatusChartData = () => {
    if (!statistics?.status_counts) return [];
    return Object.entries(statistics.status_counts).map(([status, count]) => ({
      name: status.replace('_', ' ').toUpperCase(),
      value: count,
      color: statusColors[status] || '#757575'
    }));
  };
  
  // Prepare chart data for damage type breakdown
  const prepareDamageTypeChartData = () => {
    if (!statistics?.damage_type_counts) return [];
    return Object.entries(statistics.damage_type_counts).map(([type, count]) => ({
      name: type.replace('_', ' ').toUpperCase(),
      count: count
    }));
  };
  
  // Calculate percentages
  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!statistics) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          No statistics available
        </Typography>
      </Box>
    );
  }
  
  const statusChartData = prepareStatusChartData();
  const damageTypeChartData = prepareDamageTypeChartData();
  const totalLocations = statistics.total_locations || 0;
  
  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Real-time statistics and data summaries
      </Typography>
      
      {/* Total Locations Card */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Total Locations
            </Typography>
            <Typography variant="h3" fontWeight="bold" sx={{ mt: 1 }}>
              {totalLocations}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
              Geo-tagged applications
            </Typography>
          </Box>
          <LocationIcon sx={{ fontSize: 60, opacity: 0.3 }} />
        </Box>
      </Paper>
      
      {/* Status Breakdown */}
      <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        Status Distribution
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(statistics.status_counts || {}).map(([status, count]) => {
          const percentage = calculatePercentage(count, totalLocations);
          const statusIcons = {
            'pending': <PendingIcon />,
            'in_progress': <ProgressIcon />,
            'approved': <ApprovedIcon />,
            'completed': <ApprovedIcon />,
            'rejected': <RejectedIcon />
          };
          
          return (
            <Grid size={{ xs: 12 }} key={status}>
              <Card elevation={2}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ color: statusColors[status], mr: 1 }}>
                      {statusIcons[status] || <LocationIcon />}
                    </Box>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1 }}>
                      {status.replace('_', ' ').toUpperCase()}
                    </Typography>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        bgcolor: statusColors[status],
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 40
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(percentage)}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: statusColors[status]
                      }
                    }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                    {percentage}% of total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      {/* Status Pie Chart */}
      {statusChartData.length > 0 && (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Status Overview
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={statusChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={
                  ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={60}
                fontSize={10}
                fill="#8884d8"
                dataKey="value"
              >
                {statusChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      )}
      
      <Divider sx={{ my: 3 }} />
      
      {/* Damage Type Breakdown */}
      <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        Damage Type Distribution
      </Typography>
      
      {damageTypeChartData.length > 0 ? (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={damageTypeChartData}>
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                style={{ fontSize: '10px' }}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#667eea" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          
          <Box sx={{ mt: 2 }}>
            {damageTypeChartData.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1,
                  bgcolor: index % 2 === 0 ? '#f5f5f5' : 'transparent',
                  borderRadius: 1
                }}
              >
                <Typography variant="caption">{item.name}</Typography>
                <Chip
                  label={item.count}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              </Box>
            ))}
          </Box>
        </Paper>
      ) : (
        <Paper elevation={1} sx={{ p: 2, mb: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            No damage type data available
          </Typography>
        </Paper>
      )}
      
      {/* Quick Stats Summary */}
      <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Quick Summary
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          <Chip
            icon={<LocationIcon />}
            label={`${totalLocations} Total`}
            color="primary"
            size="small"
          />
          <Chip
            icon={<CompletedIcon />}
            label={`${statistics.status_counts?.completed || 0} Completed`}
            sx={{ bgcolor: '#4CAF50', color: 'white' }}
            size="small"
          />
          <Chip
            icon={<ApprovedIcon />}
            label={`${statistics.status_counts?.approved || 0} Approved`}
            sx={{ bgcolor: '#8BC34A', color: 'white' }}
            size="small"
          />
          <Chip
            icon={<ProgressIcon />}
            label={`${statistics.status_counts?.processing || 0} In Progress`}
            sx={{ bgcolor: '#2196F3', color: 'white' }}
            size="small"
          />
          <Chip
            icon={<PendingIcon />}
            label={`${statistics.status_counts?.under_review || 0} Under Review`}
            sx={{ bgcolor: '#FFA500', color: 'white' }}
            size="small"
          />
          <Chip
            icon={<RejectedIcon />}
            label={`${statistics.status_counts?.rejected || 0} Rejected`}
            sx={{ bgcolor: '#F44336', color: 'white' }}
            size="small"
          />
        </Box>
      </Paper>
      
      {/* Export Options */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
        <Typography variant="caption" color="primary" fontWeight="bold">
          <TrendingIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
          Statistics are updated in real-time based on applied filters
        </Typography>
      </Box>
    </Box>
  );
};

export default StatisticsPanel;
